import { defineStore } from 'pinia';
import _ from 'lodash';
import ticketApi from '@/api/ticketApi';
import { useToast } from 'vue-toast-notification';
import i18n from '@/i18n';

const toast = useToast();
const t = i18n.global.t;
const ticketApiService = new ticketApi();

export const useTicketStore = defineStore(
  `${process.env.VUE_APP_NAME}_ticketStore`,
  {
    id: 'ticket',
    state: () => ({
      layouts: {},
      fields: {},
      statuses: {},
      workflows: {},
      screens: {},
      optionMatrixs: {},
      priority: {},
      ticketListCache: {},
    }),
    persist: true,
    actions: {
      async fetchLayout(refresh = false) {
        if (refresh || Object.keys(this.layouts).length === 0) {
          this.layouts = await ticketApiService.getAllLayout();
        } else {
          var updated = false;
          await Promise.all(
            this.layouts.layouts.map(async (item, index) => {
              var request = {
                id: item.id,
                revision: item.revision,
              };

              var result = await ticketApiService.checkRevisionLayout(request);

              if (!result.isUpdate) {
                this.layouts.layouts[index] = result.layout;
                updated = true;

                await this.updateMasterData('layout');
              }
            })
          );
        }

        return _.cloneDeep(this.layouts.layouts);
      },
      async fetchField(refresh = false) {
        if (refresh || Object.keys(this.fields).length === 0)
          this.fields = await ticketApiService.getAllField();
        else {
          var result = await ticketApiService.checkUpdateField();

          if (result != this.fields.lastUpdate) {
            await this.updateMasterData('field');
          }
        }

        return JSON.parse(JSON.stringify(this.fields));
      },
      async fetchStatus(refresh = false) {
        if (refresh || Object.keys(this.statuses).length === 0)
          this.statuses = await ticketApiService.getAllStatus();
        else {
          var maxDateObject = this.statuses.reduce((max, item) =>
            new Date(item.updatedDateTime) > new Date(max.updatedDateTime)
              ? item
              : max
          );

          var result = await ticketApiService.checkUpdateStatus();

          if (result != maxDateObject.updatedDateTime) {
            await this.updateMasterData('status');
          }
        }

        return JSON.parse(JSON.stringify(this.statuses));
      },
      async fetchWorkflow(refresh = false) {
        if (refresh || Object.keys(this.workflows).length === 0)
          this.workflows = await ticketApiService.getAllWorkFlow();
        else {
          var result = await ticketApiService.checkUpdateWorkflow();

          if (result != this.workflows.lastUpdate) {
            await this.updateMasterData('workflow');
          }
        }

        return JSON.parse(JSON.stringify(this.workflows));
      },
      async fetchScreen(refresh = false) {
        if (refresh || Object.keys(this.screens).length === 0)
          this.screens = await ticketApiService.getAllScreen();
        else {
          var result = await ticketApiService.checkUpdateScreen();

          if (result != this.screens.lastUpdate) {
            await this.updateMasterData('screen');
          }
        }

        return JSON.parse(JSON.stringify(this.screens));
      },
      async fetchOptionMatrix(refresh = false) {
        if (refresh || Object.keys(this.optionMatrixs).length === 0)
          this.optionMatrixs = await ticketApiService.getAllOptionMatrix();

        return JSON.parse(JSON.stringify(this.optionMatrixs));
      },
      async fetchPriority(refresh = false) {
        if (refresh || Object.keys(this.priority).length === 0)
          this.priority = await ticketApiService.getAllPriority();
        else {
          var maxDateObject = this.priority.reduce((max, item) =>
            new Date(item.updatedDateTime) > new Date(max.updatedDateTime)
              ? item
              : max
          );

          var result = await ticketApiService.checkUpdatePriority();

          if (result != maxDateObject.updatedDateTime) {
            await this.updateMasterData('priority');
          }
        }

        return JSON.parse(JSON.stringify(this.priority));
      },
      getTicketListCacheKey(context) {
        const { page, ...restFilters } = context.filters ?? {};

        return JSON.stringify({
          pageConfigId: context.pageConfigId,
          projectIds: context.projectIds,
          isMyTicket: context.isMyTicket,
          filters: restFilters,
          filterReport: context.filterReport,
          quickFilterId: context.quickFilterId ?? null,
          tab: context.tab,
          viewModeFilter: context.viewModeFilter,
        });
      },
      getCachedTicketList(pageConfigId, cacheKey) {
        const cached = this.ticketListCache[pageConfigId];
        if (!cached || cached.cacheKey !== cacheKey) return null;

        return {
          tickets: _.cloneDeep(cached.tickets),
          pagination: { ...cached.pagination },
        };
      },
      getCachedTicketListByPage(pageConfigId) {
        const cached = this.ticketListCache[pageConfigId];
        if (!cached) return null;

        return {
          tickets: _.cloneDeep(cached.tickets),
          pagination: { ...cached.pagination },
          cacheContext: cached.cacheContext
            ? _.cloneDeep(cached.cacheContext)
            : null,
          availableFields: cached.availableFields
            ? _.cloneDeep(cached.availableFields)
            : [],
        };
      },
      saveTicketListCache(pageConfigId, cacheKey, data) {
        this.ticketListCache[pageConfigId] = {
          cacheKey,
          tickets: _.cloneDeep(data.tickets),
          pagination: { ...data.pagination },
          cacheContext: data.cacheContext
            ? _.cloneDeep(data.cacheContext)
            : null,
          availableFields: data.availableFields
            ? _.cloneDeep(data.availableFields)
            : [],
          timestamp: Date.now(),
        };
      },
      async updateMasterData(type) {
        switch (type) {
          case 'field':
            this.fields = await ticketApiService.getAllField();
            this.layouts = await ticketApiService.getAllLayout();
            this.screens = await ticketApiService.getAllScreen();
            this.workflows = await ticketApiService.getAllWorkFlow();
            break;
          case 'layout':
            this.statuses = await ticketApiService.getAllStatus();
            this.optionMatrixs = await ticketApiService.getAllOptionMatrix();
            break;
          case 'screen':
            this.layouts = await ticketApiService.getAllLayout();
            this.screens = await ticketApiService.getAllScreen();
            this.workflows = await ticketApiService.getAllWorkFlow();
            break;
          case 'priority':
            this.layouts = await ticketApiService.getAllLayout();
            this.priority = await ticketApiService.getAllPriority();
            break;
          case 'status':
            this.layouts = await ticketApiService.getAllLayout();
            this.statuses = await ticketApiService.getAllStatus();
            break;
          case 'workflow':
            this.layouts = await ticketApiService.getAllLayout();
            this.workflows = await ticketApiService.getAllWorkFlow();
            break;
          default:
            break;
        }

        toast.open({
          message: `<strong>${t('common.notification')}</strong><br>${t(
            'common.fieldNameUpdated',
            {
              fieldName: t('ticket.masterData'),
            }
          )}`,
          type: 'success',
          duration: 5000,
          position: 'bottom-right',
        });
      },
    },
  }
);
